import { useCallback, useMemo } from 'react';

import { useEnableExpandedTaxIdCountries } from '../../../../feature-flags/hooks/use-enable-expanded-tax-id-countries';
import { CountryConfigNames } from '../../../../contexts/country-config-context/country-config-enums';
import { CountryUIConfigName } from '../../../../contexts/country-config-context/country-ui-config/constants';
import { useCountryConfig } from '../../../../hooks/use-country-config';
import { TaxIdCountryConfig, TaxIdentifierName, defaultTaxIdentifierName } from '../constants/tax-identifier';

export const useTaxIdConfig = (issuingAuthority) => {
  // TODO: [SPD-26173] Remove once enableExpandedTaxIdCountries is fully rolled out
  const isExpandedTaxIdCountriesEnabled = useEnableExpandedTaxIdCountries();
  const [isTaxIdCountryConfigAvailable, taxIdConfigData] = useCountryConfig(CountryConfigNames.TaxIdRequired);
  // TODO: [SPD-26173] Remove once enableExpandedTaxIdCountries is fully rolled out
  const [, taxIdUIOverrideData] = useCountryConfig(CountryUIConfigName.TaxIdUIOverride);
  const [, taxInformationMessageData] = useCountryConfig(CountryUIConfigName.TaxInformationMessage);

  const {
    taxIdName: taxIdNameConfig,
    taxIdMask: taxIdMaskConfig,
    invoiceAddressRequired: invoiceAddressRequiredConfig,
    visibleForPaymentFlow: visibleForPaymentFlowConfig,
    showIssuingAuthorityList: showIssuingAuthorityListConfig,
    defaultIssuingAuthority,
    taxIdRegex: taxIdRegexConfig,
    taxIdEditAllowed,
  } = taxIdConfigData || {};

  const taxIdName = issuingAuthority
    ? (TaxIdentifierName[issuingAuthority] ?? defaultTaxIdentifierName)
    : (taxIdNameConfig ?? defaultTaxIdentifierName);
  const taxIdMask = (issuingAuthority && TaxIdCountryConfig[issuingAuthority]?.mask) || taxIdMaskConfig;
  const taxIdRegex = (issuingAuthority && TaxIdCountryConfig[issuingAuthority]?.regex) || taxIdRegexConfig;

  // TODO: [SPD-26173] Remove once enableExpandedTaxIdCountries is fully rolled out
  const isInvoiceAddressRequired = useMemo(() => {
    if (!isExpandedTaxIdCountriesEnabled && taxIdUIOverrideData?.invoiceAddressRequired !== undefined) {
      return taxIdUIOverrideData.invoiceAddressRequired === 'true';
    }
    return invoiceAddressRequiredConfig?.toLowerCase() === 'true';
  }, [isExpandedTaxIdCountriesEnabled, taxIdUIOverrideData, invoiceAddressRequiredConfig]);

  // TODO: [SPD-26173] Remove once enableExpandedTaxIdCountries is fully rolled out
  const shouldShowIssuingAuthorityList = useMemo(() => {
    if (!isExpandedTaxIdCountriesEnabled && taxIdUIOverrideData?.showIssuingAuthorityList !== undefined) {
      return taxIdUIOverrideData.showIssuingAuthorityList === 'true';
    }
    return isExpandedTaxIdCountriesEnabled ? showIssuingAuthorityListConfig?.toLowerCase() === 'true' : isInvoiceAddressRequired;
  }, [isExpandedTaxIdCountriesEnabled, taxIdUIOverrideData, showIssuingAuthorityListConfig, isInvoiceAddressRequired]);

  const effectiveDefaultIssuingAuthority = isExpandedTaxIdCountriesEnabled ? defaultIssuingAuthority : undefined;

  const isTaxIdConfigDataEmpty = useMemo(() => taxIdConfigData && Object.keys(taxIdConfigData).length === 0, [taxIdConfigData]);
  const baseTaxIdAvailable = useMemo(
    () => !isTaxIdConfigDataEmpty && isTaxIdCountryConfigAvailable,
    [isTaxIdConfigDataEmpty, isTaxIdCountryConfigAvailable],
  );

  // TODO: [SPD-26173] Remove once enableExpandedTaxIdCountries is fully rolled out
  const isTaxIdAvailable = useMemo(() => {
    if (!isExpandedTaxIdCountriesEnabled && taxIdUIOverrideData?.isTaxIdAvailable !== undefined) {
      return taxIdUIOverrideData.isTaxIdAvailable === 'true';
    }
    return baseTaxIdAvailable;
  }, [isExpandedTaxIdCountriesEnabled, taxIdUIOverrideData, baseTaxIdAvailable]);

  const baseVisibleForPaymentFlow = useMemo(
    () => baseTaxIdAvailable && visibleForPaymentFlowConfig?.toLowerCase() === 'true',
    [baseTaxIdAvailable, visibleForPaymentFlowConfig],
  );

  // TODO: [SPD-26173] Remove once enableExpandedTaxIdCountries is fully rolled out
  const isVisibleForPaymentFlow = useMemo(() => {
    if (!isExpandedTaxIdCountriesEnabled && taxIdUIOverrideData?.isVisibleForPaymentFlow !== undefined) {
      return taxIdUIOverrideData.isVisibleForPaymentFlow === 'true';
    }
    return baseVisibleForPaymentFlow;
  }, [isExpandedTaxIdCountriesEnabled, taxIdUIOverrideData, baseVisibleForPaymentFlow]);

  const isValidTaxId = useCallback(
    (value, selectedIssuingAuthority) => {
      if (!value || !taxIdRegex) {
        return false;
      }
      const regex = (selectedIssuingAuthority && TaxIdCountryConfig[selectedIssuingAuthority]?.regex) || taxIdRegex;
      return new RegExp(regex, 'i').test(value);
    },
    [taxIdRegex],
  );

  return useMemo(
    () => ({
      isTaxIdAvailable,
      isValidTaxId,
      taxIdName,
      taxIdMask,
      sellerTaxIdName: taxIdNameConfig ?? defaultTaxIdentifierName,
      isInvoiceAddressRequired,
      isVisibleForPaymentFlow,
      shouldShowIssuingAuthorityList,
      defaultIssuingAuthority: effectiveDefaultIssuingAuthority,
      isTaxIdEditAllowed: taxIdEditAllowed?.toLowerCase() === 'true',
      taxInformationMessageDescriptor: taxInformationMessageData?.message,
    }),
    [
      isTaxIdAvailable,
      isValidTaxId,
      taxIdName,
      taxIdMask,
      taxIdNameConfig,
      isInvoiceAddressRequired,
      isVisibleForPaymentFlow,
      shouldShowIssuingAuthorityList,
      effectiveDefaultIssuingAuthority,
      taxIdEditAllowed,
      taxInformationMessageData,
    ],
  );
};
